import { useMemo, useState } from "react";
import { useSelector } from "react-redux";

import * as Api from "@/api";
import urls from "@/constants/Urls";
import Avatar from "@/design/components/Avatar";
import useAudio from "@/utils/hooks/useAudio";
import useColor from "@/utils/hooks/useColor";
import Valkyrie, {
  viArrowRotateRight,
  viCircleExclamation,
  viMusic,
  viPause,
  viPlay,
  viSpinnerThird,
  viTrashCan,
} from "@sippy-platform/valkyrie";

import { useSortable } from "@dnd-kit/react/sortable";
import {
  Box,
  Card,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Slider,
  Stack,
} from "@mui/material";
import PropTypes from "prop-types";

function Sound({ entry, sound, id, musicOnHold, musicOnHoldDispatch, index }) {
  const { background } = useColor(sound?.name || "");

  const { ref, isDragging } = useSortable({ id, index });

  const pbx = useSelector((state) => state.domain.pbx);

  const src = useMemo(
    () => `${urls.audio}${pbx.context}%2F${sound?.id}.${sound?.contentType}?alt=media&token=${sound?.token}`,
    [pbx.context, sound?.contentType, sound?.id, sound?.token],
  );

  const { playable, duration, playPause, progress, secondsToTime, seek, seekFinish, status } = useAudio(src);

  // Deleting
  const [pending, setPending] = useState(false);

  const deleteSound = async (entry) => {
    setPending(true);

    Promise.resolve(Api.deleteMusicOnHoldEntry(musicOnHold, entry))
      .then(() => musicOnHoldDispatch({ type: "removeSound", entry }))
      .finally(() => setPending(false));
  };

  return (
    <Card
      ref={ref}
      variant="outlined"
      sx={(theme) => ({
        border: "none",
        position: "relative",
        contain: "paint",
        "&:nth-of-type(odd)": {
          backgroundColor: "#f8f8f8",
          ...theme.applyStyles("dark", {
            backgroundColor: "#28282d",
          }),
        },
        "&:hover": {
          backgroundColor: "#efefef",
          ...theme.applyStyles("dark", {
            backgroundColor: "#323237",
          }),
        },
        ...(isDragging && {
          boxShadow: 1,
          backdropFilter: "blur(5px)",
          backgroundColor: "rgb(248 248 248 / .5) !important",
          ...theme.applyStyles("dark", {
            backgroundColor: "rgb(40 40 45 / .5) !important",
          }),
          position: "relative",
          zIndex: 1,
        }),
      })}
    >
      <List>
        <ListItem
          disablePadding
          secondaryAction={
            <Stack
              direction="row"
              sx={{
                gap: 0.25,
              }}
            >
              <IconButton onClick={playPause} disabled={!playable} color="inherit">
                <Valkyrie
                  icon={
                    status === "stopped"
                      ? viArrowRotateRight
                      : status === "paused"
                        ? viPlay
                        : status === "error"
                          ? viCircleExclamation
                          : viPause
                  }
                />
              </IconButton>
              <IconButton onClick={() => deleteSound(entry)} disabled={pending} color="inherit">
                <Valkyrie icon={pending ? viSpinnerThird : viTrashCan} spin={pending} />
              </IconButton>
            </Stack>
          }
        >
          <ListItemButton sx={{ cursor: entry?.id ? "move" : "not-allowed", borderRadius: 0 }}>
            <ListItemAvatar>
              <Avatar text={sound?.name} icon={viMusic} />
            </ListItemAvatar>
            <ListItemText
              primary={sound?.name}
              secondary={
                <>
                  {secondsToTime(progress)}
                  <Box as="span" sx={{ opacity: 0.75 }}>
                    {" "}
                    / {secondsToTime(duration)}
                  </Box>
                </>
              }
            />
          </ListItemButton>
        </ListItem>
      </List>
      <Slider
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "100%",
          backgroundColor: "transparent",
          transition: "all .1s ease-in",
          height: 4,
          p: 0,
          "&:hover": {
            backgroundColor: background[100],
          },
          ".MuiSlider-rail": {
            height: 4,
            opacity: 0,
          },
          ".MuiSlider-track": {
            height: 4,
            backgroundColor: background[500],
          },
          ".MuiSlider-thumb": {
            display: "none",
          },
        }}
        value={progress}
        onChange={(e, value) => seek(value)}
        onChangeCommitted={seekFinish}
        min={0}
        max={duration}
        disabled={!playable}
      />
    </Card>
  );
}

Sound.propTypes = {
  entry: PropTypes.object,
  sound: PropTypes.object,
  musicOnHold: PropTypes.object,
  musicOnHoldDispatch: PropTypes.func,
  id: PropTypes.number,
  index: PropTypes.number,
};

export default Sound;
